import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents, useMap } from 'react-leaflet';
import L, { Icon } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import Swal from 'sweetalert2';
import { api, Sensor, StatisticsData, StatisticsParams } from './services/api';
import Hls from 'hls.js';
import SensorDetailPanel from './components/SensorDetailPanel';
import StreamOverlay from './components/StreamOverlay';
import SensorHoverTooltip from './components/SensorHoverTooltip';

// 修復 Leaflet 圖標問題
delete (Icon.Default.prototype as any)._getIconUrl;
Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// 地圖點選處理器組件（用於新增感測器表單）
const MapClickHandler = ({ onMapClick }: { onMapClick: (lat: number, lng: number) => void }) => {
  useMapEvents({
    click: (e: any) => {
      onMapClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

// 自動縮放地圖以顯示所有感測器
const MapAutoFit = ({ sensors }: { sensors: Sensor[] }) => {
  const map = useMap();
  const hasFitted = useRef(false);

  useEffect(() => {
    if (sensors.length > 0 && !hasFitted.current) {
      const bounds = sensors.map(s => s.coordinates);
      if (bounds.length > 0) {
        map.fitBounds(bounds as L.LatLngBoundsExpression, { padding: [50, 50] });
        hasFitted.current = true;
      }
    }
  }, [sensors, map]);

  return null;
};

// 感測器圖示映射函數
const getSensorIcon = (sensorType: string, status: string, zoomLevel: number = 13) => {
  const iconMap: { [key: string]: string } = {
    'power_sensor': '/images/sensors/power_sensor.png',
    'power_sensor_alarm': '/images/sensors/power_sensor_alarm.png',
    'water_sensor': '/images/sensors/water_sensor.png',
    'water_sensor_alarm': '/images/sensors/water_sensor_alarm.png',
    'smoke_sensor': '/images/sensors/smoke_sensor.png',
    'smoke_sensor_alarm': '/images/sensors/smoke_sensor_alarm.png',
    'rgb_camera': '/images/sensors/rgb_camera.png',
    'rgb_camera_alarm': '/images/sensors/rgb_camera_alarm.png',
  };

  const typeMap: { [key: string]: string } = {
    '電量感測器': 'power_sensor',
    '水位感測器': 'water_sensor',
    '煙霧感測器': 'smoke_sensor',
    'RGB攝影機': 'rgb_camera',
  };

  const statusMap: { [key: string]: string } = {
    'active': '',
    'alert': 'alarm',
  };

  const englishType = typeMap[sensorType] || sensorType;
  const englishStatus = statusMap[status] || status;
  
  let iconKey: string;
  if (englishStatus === '') {
    iconKey = englishType;
  } else {
    iconKey = `${englishType}_${englishStatus}`;
  }

  // 調試信息（僅在開發模式下輸出，避免生產環境大量日誌）
  if (process.env.NODE_ENV === 'development') {
    // 只在第一次或圖示鍵變化時輸出，避免重複日誌
    // console.log(`圖示選擇: 感測器類型=${sensorType}, 狀態=${status}, 英文類型=${englishType}, 英文狀態=${englishStatus}, 圖示鍵=${iconKey}`);
  }

  // 根據縮放級別計算圖示大小
  const baseSize = 50;
  const minSize = 25;
  const maxSize = 100;
  const sizeMultiplier = Math.max(0.5, Math.min(2, zoomLevel / 13));
  const iconSize = Math.max(minSize, Math.min(maxSize, Math.round(baseSize * sizeMultiplier)));

  return new Icon({
    iconUrl: iconMap[iconKey] || iconMap[englishType] || '/images/sensors/smoke_detector.png',
    iconSize: [iconSize, iconSize],
    iconAnchor: [iconSize / 2, iconSize],
    popupAnchor: [0, -iconSize],
  });
};

// 區域選項
const areaOptions = [
  { value: 'north-side', label: '北側' },
  { value: 'linkou-carrefour', label: '林口家樂福' },
  { value: 'linkou-old-downtown', label: '林口舊市區' },
  { value: 'liyuan-elementary', label: '麗園國小' },
  { value: 'a7-station', label: 'A7站重劃區-樂善國小' },
];

// 感測器類型選項
const sensorTypes = ['電量感測器', '水位感測器', '煙霧感測器', 'RGB攝影機'];

// 簡化的範圍設定組件
const RangeSlider = ({ 
  min, 
  max, 
  value, 
  onChange, 
  label, 
  unit, 
  currentValue 
}: { 
  min: number; 
  max: number; 
  value: { normalMin: number; normalMax: number }; 
  onChange: (value: { normalMin: number; normalMax: number }) => void; 
  label: string; 
  unit: string; 
  currentValue: number; 
}) => {
  const handleNormalMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newMin = Number(e.target.value);
    console.log('修改最小值:', newMin, '類型:', typeof newMin);
    onChange({ ...value, normalMin: newMin });
  };

  const handleNormalMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newMax = Number(e.target.value);
    console.log('修改最大值:', newMax, '類型:', typeof newMax);
    onChange({ ...value, normalMax: newMax });
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        <span className="text-xs text-gray-500">當前: {currentValue}</span>
      </div>
      
      <div className="space-y-2">
        <div className="text-xs text-green-600 font-medium">正常範圍</div>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={min}
            max={max}
            value={value.normalMin}
            onChange={handleNormalMinChange}
            className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            placeholder={`最小值 (${min}-${max})`}
          />
          <span className="text-xs text-gray-500">至</span>
          <input
            type="number"
            min={min}
            max={max}
            value={value.normalMax}
            onChange={handleNormalMaxChange}
            className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            placeholder={`最大值 (${min}-${max})`}
          />
          <span className="text-xs text-gray-500">{unit}</span>
        </div>
        <div className="text-xs text-green-600">
          正常範圍: {value.normalMin} - {value.normalMax} {unit}
        </div>
      </div>
    </div>
  );
};

function App() {
  const [startDate, setStartDate] = useState('2026-06-17');
  const [endDate, setEndDate] = useState('2026-06-17');
  const [selectedArea, setSelectedArea] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSensorType, setSelectedSensorType] = useState('');
  const [sensors, setSensors] = useState<Sensor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSensor, setSelectedSensor] = useState<Sensor | null>(null);
  const [hoveredSensor, setHoveredSensor] = useState<Sensor | null>(null);
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });
  const [showThresholdSettings, setShowThresholdSettings] = useState(false);
  const [mapZoom, setMapZoom] = useState(13);
  const [mapCenter, setMapCenter] = useState<[number, number]>([25.0330, 121.5654]);
  const [streamFullscreen, setStreamFullscreen] = useState(false);
  
  // RGB 攝影機即時影像
  const [rgbCameraImageUrl, setRgbCameraImageUrl] = useState<string | null>(null);
  const [rgbCameraImageError, setRgbCameraImageError] = useState(false);
  // RGB 攝影機事件影片（當有警報時）
  const [eventVideoUrl, setEventVideoUrl] = useState<string | null>(null);
  
  // 新增感測器彈窗狀態
  const [showAddSensorModal, setShowAddSensorModal] = useState(false);
  const [newSensorForm, setNewSensorForm] = useState({
    name: '',
    type: '電量感測器',
    location_name: '',
    latitude: '',
    longitude: '',
    ip: '',
    camera_type: 'ip', // 'ip' 或 'nvr'
    // IP 攝影機相關欄位
    ip_address: '',
    ip_port: '',
    ipcam_username: 'admin',
    ipcam_password: 'Pass1234',
    rtsp_url: '',
    // NVR 攝影機相關欄位
    nvr_host: '',
    nvr_port: '7001',
    nvr_username: 'admin',
    nvr_password: '',
    nvr_camera_id: '',
  });
  
  // 新增感測器地圖相關狀態
  const [selectedLocation, setSelectedLocation] = useState<[number, number] | null>(null);
  const addSensorMapRef = useRef<any>(null);
  
  // 狀態過濾器：'all' | 'active' | 'alert'
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'alert'>('all');
  
  // 警報通知視窗狀態
  const [alertNotifications, setAlertNotifications] = useState<Array<{
    id: number;
    sensor: Sensor;
    timestamp: Date;
    event_video_url?: string | null;
    event_description?: string | null;
  }>>([]);
  
  // 跑馬燈狀態
  const [currentAlertIndex, setCurrentAlertIndex] = useState(0);
  const alertIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const previousAlertIdsRef = useRef<Set<number>>(new Set());
  
  // 地圖引用
  const mapRef = useRef<any>(null);
  
  // 統計彈窗狀態
  const [showStatisticsModal, setShowStatisticsModal] = useState(false);
  const [statisticsData, setStatisticsData] = useState<StatisticsData | null>(null);
  const [statisticsLoading, setStatisticsLoading] = useState(false);
  const [statisticsParams, setStatisticsParams] = useState<StatisticsParams>({
    start_time: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    end_time: new Date().toISOString(),
    area: 'all',
    sensor_type: 'all'
  });
  
  // 範圍滑塊狀態
  const [rangeSettings, setRangeSettings] = useState({
    power_sensor: {
      temperature: { normalMin: 20, normalMax: 35 },
      humidity: { normalMin: 30, normalMax: 70 },
      battery: { normalMin: 3.0, normalMax: 4.2 }
    },
    water_sensor: {
      water_level: { normalMin: 0, normalMax: 10 }
    },
    smoke_sensor: {
      smoke: { normalMin: 0, normalMax: 50 }
    },
    rgb_camera: {
      motion: { normalMin: 0, normalMax: 3 },
      light: { normalMin: 30, normalMax: 70 }
    }
  });

  useEffect(() => {
    fetchSensors(true); // 首次載入時顯示載入動畫
    
    // 定期輪詢感測器數據（每5秒更新一次，確保及時檢測到新警報）
    const interval = setInterval(() => {
      fetchSensors(false); // 後續輪詢不顯示載入動畫
    }, 5000);
    
    return () => {
      clearInterval(interval);
    };
  }, []);

  // 點擊地圖空白區域重置過濾器
  useEffect(() => {
    const handleMapClick = (e: Event) => {
      const target = e.target as HTMLElement;
      // 如果點擊的是感測器標記、彈窗、控制項或其他互動元素，不重置
      if (
        target.closest('.leaflet-marker-icon') || // 地圖標記
        target.closest('.leaflet-popup') || // 地圖彈窗
        target.closest('.leaflet-control') || // 地圖控制項
        target.closest('.leaflet-interactive') // 地圖互動元素（如圖層）
      ) {
        return;
      }
      // 點擊地圖的空白區域時重置過濾器
      if (target.closest('.leaflet-container') || target.closest('.leaflet-pane')) {
        setStatusFilter('all');
      }
    };

    // 延遲添加監聽器，確保地圖已載入
    const timer = setTimeout(() => {
      const mapElement = document.querySelector('.leaflet-container');
      if (mapElement) {
        mapElement.addEventListener('click', handleMapClick);
      }
    }, 500);

    return () => {
      clearTimeout(timer);
      const mapElement = document.querySelector('.leaflet-container');
      if (mapElement) {
        mapElement.removeEventListener('click', handleMapClick);
      }
    };
  }, []);

  // 監聽新警報觸發
  useEffect(() => {
    if (sensors.length === 0) {
      // 如果還沒有感測器數據，初始化 previousAlertIdsRef 為空
      previousAlertIdsRef.current = new Set();
      return;
    }
    
    const alertSensors = sensors.filter(s => s.status === 'alert');
    const currentAlertIds = new Set(alertSensors.map(s => s.id));
    
    // 檢查是否有新的警報（不在 previousAlertIdsRef 中的警報）
    // 如果 previousAlertIdsRef 是空的，表示是第一次載入，所有警報都視為"新"警報
    const isFirstLoad = previousAlertIdsRef.current.size === 0;
    
    alertSensors.forEach(sensor => {
      if (isFirstLoad || !previousAlertIdsRef.current.has(sensor.id)) {
        // 新警報觸發，添加到通知列表
        console.log('🚨 新警報觸發:', sensor.name, sensor.alert, sensor.status, isFirstLoad ? '(首次載入)' : '(新觸發)');
        setAlertNotifications(prev => {
          // 避免重複添加
          if (prev.some(n => n.id === sensor.id)) {
            console.log('警報通知已存在，跳過:', sensor.id);
            return prev;
          }
          console.log('添加新警報通知:', sensor.id, sensor.name);
          return [...prev, {
            id: sensor.id,
            sensor: sensor,
            timestamp: new Date(),
            event_video_url: (sensor as any).event_video_url,
            event_description: (sensor as any).event_description,
          }];
        });
      }
    });
    
    // 移除已解決的警報通知（如果感測器狀態不再是 alert）
    setAlertNotifications(prev => {
      return prev.filter(notification => {
        const currentSensor = sensors.find(s => s.id === notification.id);
        // 如果感測器不存在或狀態不再是 alert，保留通知（讓用戶手動關閉）
        // 或者可以選擇自動移除：return currentSensor?.status === 'alert';
        return true; // 保留所有通知，直到用戶手動關閉
      });
    });
    
    // 更新已記錄的警報ID
    previousAlertIdsRef.current = currentAlertIds;
  }, [sensors]);

  // 跑馬燈效果
  useEffect(() => {
    const alertSensors = sensors.filter(s => s.status === 'alert');
    
    if (alertSensors.length > 0) {
      alertIntervalRef.current = setInterval(() => {
        setCurrentAlertIndex(prev => (prev + 1) % alertSensors.length);
      }, 3000); // 每3秒切換一次
    }

    return () => {
      if (alertIntervalRef.current) {
        clearInterval(alertIntervalRef.current);
      }
    };
  }, [sensors]);

  // 使用 ref 來追蹤上一次的感測器數據，避免不必要的更新
  const previousSensorsRef = useRef<string>('');
  
  const fetchSensors = async (showLoading: boolean = false) => {
    try {
      if (showLoading) {
        setLoading(true);
        setError(null); // 清除之前的錯誤
      }
      
      const data = await api.getDashboardData();
      
      // 計算同位置感測器的偏移，避免圖示重疊
      const locationCount: Record<string, number> = {};
      const offsets = [
        [0, 0], [0.0003, 0.0003], [-0.0003, 0.0003], [0.0003, -0.0003], [-0.0003, -0.0003]
      ];

      const transformedData = data.sensors.map((sensor: any) => {
        const lat = parseFloat(sensor.latitude) || (sensor.coordinates ? sensor.coordinates[0] : 0);
        const lng = parseFloat(sensor.longitude) || (sensor.coordinates ? sensor.coordinates[1] : 0);
        const locKey = `${lat.toFixed(3)}_${lng.toFixed(3)}`;
        const idx = locationCount[locKey] || 0;
        locationCount[locKey] = idx + 1;
        const [dLat, dLng] = offsets[idx] || [idx * 0.0003, 0];

        return {
        id: sensor.id,
        name: sensor.name,
        type: sensor.type || '未知類型',
        location: sensor.location || sensor.location_name || '未知位置',
        location_name: sensor.location_name || sensor.location || '未知位置',
        coordinates: [lat + dLat, lng + dLng] as [number, number],
        status: sensor.status || 'active',
        alert: sensor.alert || '正常',
        battery: sensor.battery ?? null,
        ip: sensor.ip || `192.168.1.${100 + sensor.id}`,
        lastUpdate: sensor.lastUpdate || sensor.created_at,
        image: sensor.image || `https://via.placeholder.com/300x200/888888/FFFFFF?text=${sensor.type || '感測器'}`,
        // RTSP 和 IP 攝影機相關
        rtsp_url: sensor.rtsp_url,
        ip_address: sensor.ip_address,
        ipcam_username: sensor.ipcam_username,
        // RGB 攝影機事件影片（當有警報時）
        event_video_url: sensor.event_video_url,
        event_description: sensor.event_description,
        // 感測器數據屬性
        temperature: sensor.temperature,
        humidity: sensor.humidity,
        co_level: sensor.co_level,
        smoke_level: sensor.smoke_level,
        // 地點與攝影機
        site_id: sensor.site_id,
        nvr_camera_id: sensor.nvr_camera_id,
        alert_triggered_at: sensor.alert_triggered_at,
        alert_event_id: sensor.alert_event_id,
      };});
      
      // 比較數據是否真的變化（使用 JSON.stringify 比較關鍵字段）
      const currentDataKey = JSON.stringify(transformedData.map(s => ({
        id: s.id,
        status: s.status,
        alert: s.alert,
        lastUpdate: s.lastUpdate
      })));
      
      // 只有數據真正變化時才更新狀態，避免不必要的重新渲染
      if (currentDataKey !== previousSensorsRef.current) {
        previousSensorsRef.current = currentDataKey;
        setSensors(transformedData);
        
        if (process.env.NODE_ENV === 'development') {
          console.log('感測器數據已更新:', transformedData.length, '個感測器');
        }
      }
      
      // 如果沒有感測器數據，顯示提示但不視為錯誤
      if (transformedData.length === 0 && showLoading) {
        setError('目前沒有感測器數據，請檢查後端服務是否正常運行');
      } else {
        setError(null);
      }
    } catch (err: any) {
      const errorMessage = err?.message || '無法載入感測器數據';
      setError(errorMessage);
      console.error('Error fetching sensors:', err);
      
      // 即使出錯，也設置空數組，讓界面至少可以顯示
      setSensors([]);
    } finally {
      // 確保無論如何都會停止載入動畫
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const handleSensorClick = useCallback((sensor: Sensor) => {
    setSelectedSensor(sensor);
    setShowThresholdSettings(false);
    loadSensorThreshold(sensor.id, sensor.type); // 點擊感測器時自動載入閾值
  }, []);

  // 當 sensors 數據更新時，同步更新 selectedSensor（確保包含最新的 event_video_url）
  useEffect(() => {
    if (selectedSensor) {
      const updatedSensor = sensors.find(s => s.id === selectedSensor.id);
      if (updatedSensor) {
        // 只有當數據真正變化時才更新，避免無限循環
        const hasChanges = 
          updatedSensor.status !== selectedSensor.status ||
          updatedSensor.event_video_url !== (selectedSensor as any).event_video_url ||
          updatedSensor.event_description !== (selectedSensor as any).event_description;
        
        if (hasChanges) {
          setSelectedSensor(updatedSensor);
        }
      }
    }
  }, [sensors, selectedSensor]);

  const loadSensorThreshold = async (sensorId: number, sensorType?: string) => {
    try {
      const threshold = await api.getSensorThreshold(sensorId);
      console.log('載入的閾值設定:', threshold);
      
      if (threshold && Object.keys(threshold).length > 0) {
        setRangeSettings(prev => {
          const newSettings = { ...prev };

          if (sensorType === '電量感測器') {
            if (threshold.temperature_min != null && threshold.temperature_max != null) {
              newSettings.power_sensor.temperature.normalMin = parseFloat(threshold.temperature_min);
              newSettings.power_sensor.temperature.normalMax = parseFloat(threshold.temperature_max);
            }
            if (threshold.humidity_min != null && threshold.humidity_max != null) {
              newSettings.power_sensor.humidity.normalMin = parseFloat(threshold.humidity_min);
              newSettings.power_sensor.humidity.normalMax = parseFloat(threshold.humidity_max);
            }
            if (threshold.battery_min != null && threshold.battery_max != null) {
              newSettings.power_sensor.battery.normalMin = parseFloat(threshold.battery_min);
              newSettings.power_sensor.battery.normalMax = parseFloat(threshold.battery_max);
            }
          } else if (sensorType === '水位感測器') {
            if (threshold.light_min != null && threshold.light_max != null) {
              newSettings.water_sensor.water_level.normalMin = parseFloat(threshold.light_min);
              newSettings.water_sensor.water_level.normalMax = parseFloat(threshold.light_max);
            }
          } else if (sensorType === '煙霧感測器') {
            if (threshold.smoke_min != null && threshold.smoke_max != null) {
              newSettings.smoke_sensor.smoke.normalMin = parseFloat(threshold.smoke_min);
              newSettings.smoke_sensor.smoke.normalMax = parseFloat(threshold.smoke_max);
            }
          } else if (sensorType === 'RGB攝影機') {
            if (threshold.light_min != null && threshold.light_max != null) {
              newSettings.rgb_camera.light.normalMin = parseFloat(threshold.light_min);
              newSettings.rgb_camera.light.normalMax = parseFloat(threshold.light_max);
            }
            if (threshold.motion_min != null && threshold.motion_max != null) {
              newSettings.rgb_camera.motion.normalMin = parseFloat(threshold.motion_min);
              newSettings.rgb_camera.motion.normalMax = parseFloat(threshold.motion_max);
            }
          }

          return newSettings;
        });
      }
    } catch (error) {
      console.error('載入閾值設定失敗:', error);
    }
  };

  const handleMouseEnter = useCallback((sensor: Sensor, event: any) => {
    setHoveredSensor(sensor);
    setHoverPosition({ x: event.clientX, y: event.clientY });
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHoveredSensor(null);
  }, []);

  // HLS 即時串流
  const [hlsUrl, setHlsUrl] = useState<string | null>(null);
  const hlsVideoRef = useRef<HTMLVideoElement | null>(null);
  const hlsInstanceRef = useRef<Hls | null>(null);

  useEffect(() => {
    const cameraId = selectedSensor?.nvr_camera_id;
    if (!selectedSensor || !cameraId) {
      setHlsUrl(null);
      if (hlsInstanceRef.current) {
        hlsInstanceRef.current.destroy();
        hlsInstanceRef.current = null;
      }
      return;
    }
    const backendBaseUrl = process.env.REACT_APP_API_URL || '';
    setHlsUrl(`${backendBaseUrl}/api/sensors/${selectedSensor.id}/hls/?camera_id=${cameraId}`);
  }, [selectedSensor]);

  useEffect(() => {
    if (!hlsUrl || !hlsVideoRef.current) return;
    const video = hlsVideoRef.current;

    if (hlsInstanceRef.current) {
      hlsInstanceRef.current.destroy();
      hlsInstanceRef.current = null;
    }

    if (Hls.isSupported()) {
      const hls = new Hls({
        liveDurationInfinity: true,
        enableWorker: true,
        lowLatencyMode: true,
      });
      hls.loadSource(hlsUrl);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        video.play().catch(() => {});
      });
      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (data.fatal) {
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
            setTimeout(() => hls.startLoad(), 3000);
          } else {
            hls.destroy();
          }
        }
      });
      hlsInstanceRef.current = hls;
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari 原生支援 HLS
      video.src = hlsUrl;
      video.play().catch(() => {});
    }

    return () => {
      if (hlsInstanceRef.current) {
        hlsInstanceRef.current.destroy();
        hlsInstanceRef.current = null;
      }
    };
  }, [hlsUrl]);

  const handleCloseInfo = () => {
    setSelectedSensor(null);
    setShowThresholdSettings(false);
    setStreamFullscreen(false);
  };

  // 處理新增感測器
  const handleAddSensor = async () => {
    try {
      // 驗證必要欄位
      if (!newSensorForm.name || !newSensorForm.type || !newSensorForm.latitude || !newSensorForm.longitude) {
        await Swal.fire({
          icon: 'error',
          title: '欄位不完整',
          text: '請填寫所有必要欄位（名稱、類型、緯度、經度）',
        });
        return;
      }

      // 準備提交資料
      const sensorData: any = {
        name: newSensorForm.name,
        type: newSensorForm.type,
        location_name: newSensorForm.location_name || '未知位置',
        latitude: parseFloat(newSensorForm.latitude),
        longitude: parseFloat(newSensorForm.longitude),
        ip: newSensorForm.ip || '',
      };

      // 如果是 RGB 攝影機，根據攝影機種類加入相應資訊
      if (newSensorForm.type === 'RGB攝影機') {
        if (newSensorForm.camera_type === 'ip') {
          // IP 攝影機配置
          if (newSensorForm.rtsp_url) {
            // 如果提供了完整的 RTSP URL，直接使用
            sensorData.rtsp_url = newSensorForm.rtsp_url;
          } else if (newSensorForm.ip_address) {
            // 使用 IP 地址、Port、帳號密碼構建 RTSP URL
            sensorData.ip_address = newSensorForm.ip_address;
            if (newSensorForm.ip_port) {
              // 如果有指定 port，構建 RTSP URL
              const username = encodeURIComponent(newSensorForm.ipcam_username || 'admin');
              const password = encodeURIComponent(newSensorForm.ipcam_password || '');
              sensorData.rtsp_url = `rtsp://${username}:${password}@${newSensorForm.ip_address}:${newSensorForm.ip_port}/stream0`;
            } else {
              // 沒有 port，使用預設格式
              sensorData.ip_address = newSensorForm.ip_address;
              sensorData.ipcam_username = newSensorForm.ipcam_username || 'admin';
              sensorData.ipcam_password = newSensorForm.ipcam_password || '';
            }
          }
        } else if (newSensorForm.camera_type === 'nvr') {
          // NVR 攝影機配置
        if (newSensorForm.nvr_host && newSensorForm.nvr_camera_id) {
          sensorData.nvr_host = newSensorForm.nvr_host;
          sensorData.nvr_port = parseInt(newSensorForm.nvr_port) || 7001;
          sensorData.nvr_username = newSensorForm.nvr_username || 'admin';
          sensorData.nvr_password = newSensorForm.nvr_password || '';
          sensorData.nvr_camera_id = newSensorForm.nvr_camera_id;
          }
        }
      }

      // 呼叫 API
      const result = await api.createSensor(sensorData);

      // 成功後關閉彈窗並重置表單
      setShowAddSensorModal(false);
      setSelectedLocation(null);
      setNewSensorForm({
        name: '',
        type: '電量感測器',
        location_name: '',
        latitude: '',
        longitude: '',
        ip: '',
        camera_type: 'ip',
        ip_address: '',
        ip_port: '',
        ipcam_username: 'admin',
        ipcam_password: 'Pass1234',
        rtsp_url: '',
        nvr_host: '',
        nvr_port: '7001',
        nvr_username: 'admin',
        nvr_password: '',
        nvr_camera_id: '',
      });

      // 顯示成功訊息
      await Swal.fire({
        icon: 'success',
        title: '新增成功！',
        text: '感測器已成功新增',
      });

      // 重新載入感測器列表
      fetchSensors(true);
    } catch (error: any) {
      console.error('新增感測器失敗:', error);
      await Swal.fire({
        icon: 'error',
        title: '新增失敗',
        text: error.message || '新增感測器時發生錯誤，請重試',
      });
    }
  };

  // 點擊警報時縮放到對應感測器
  const handleAlertClick = (sensor: Sensor) => {
    if (mapRef.current) {
      mapRef.current.setView(sensor.coordinates, 16);
      setMapCenter(sensor.coordinates);
      setMapZoom(16);
      setSelectedSensor(sensor);
    }
  };

  // 獲取統計數據
  const fetchStatisticsData = async () => {
    try {
      setStatisticsLoading(true);
      
      const data = await api.getStatisticsData(statisticsParams);
      setStatisticsData(data);
    } catch (error) {
      console.error('Error fetching statistics data:', error);
      Swal.fire({
        icon: 'error',
        title: '錯誤',
        text: '獲取統計數據時發生錯誤',
      });
    } finally {
      setStatisticsLoading(false);
    }
  };

  // 處理統計參數變更
  const handleStatisticsParamChange = (field: keyof StatisticsParams, value: string) => {
    setStatisticsParams(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDeleteSensor = async () => {
    if (!selectedSensor) return;
    
    try {
      const result = await Swal.fire({
        title: '確認永久刪除',
        text: `確定要永久刪除感測器「${selectedSensor.name}」嗎？此操作無法撤銷！`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: '確定刪除',
        cancelButtonText: '取消'
      });

      if (result.isConfirmed) {
        await api.deleteSensor(selectedSensor.id);
        
        setSensors(prev => prev.filter(sensor => sensor.id !== selectedSensor.id));
        setSelectedSensor(null);
        setStreamFullscreen(false);
        
        Swal.fire({
          icon: 'success',
          title: '刪除成功！',
          text: '感測器已成功刪除',
          confirmButtonText: '確定',
          timer: 2000,
          timerProgressBar: true
        });
      }
    } catch (error) {
      console.error('刪除感測器失敗:', error);
      Swal.fire({
        icon: 'error',
        title: '刪除失敗',
        text: '刪除感測器時發生錯誤，請重試'
      });
    }
  };

  const handleRangeChange = (sensorType: string, param: string, newValue: { normalMin: number; normalMax: number }) => {
    setRangeSettings(prev => ({
      ...prev,
      [sensorType]: {
        ...prev[sensorType as keyof typeof prev],
        [param]: newValue
      }
    }));
  };

  const handleSaveThreshold = async () => {
    if (!selectedSensor) return;
    
    console.log('保存閾值設定 - 感測器信息:', {
      id: selectedSensor.id,
      name: selectedSensor.name,
      type: selectedSensor.type
    });
    
    let validationError = '';
    
    if (selectedSensor.type === '電量感測器') {
      const t = rangeSettings.power_sensor.temperature;
      const h = rangeSettings.power_sensor.humidity;
      const b = rangeSettings.power_sensor.battery;
      if (t.normalMin >= t.normalMax) validationError = '溫度範圍設定錯誤';
      else if (h.normalMin >= h.normalMax) validationError = '濕度範圍設定錯誤';
      else if (b.normalMin >= b.normalMax) validationError = '電壓範圍設定錯誤';
    } else if (selectedSensor.type === '水位感測器') {
      const w = rangeSettings.water_sensor.water_level;
      if (w.normalMin >= w.normalMax) validationError = '水位範圍設定錯誤';
    } else if (selectedSensor.type === '煙霧感測器') {
      const s = rangeSettings.smoke_sensor.smoke;
      if (s.normalMin >= s.normalMax) validationError = '煙霧濃度範圍設定錯誤';
    } else if (selectedSensor.type === 'RGB攝影機') {
      const lightRange = rangeSettings.rgb_camera.light;
      const motionRange = rangeSettings.rgb_camera.motion;
      if (lightRange.normalMin >= lightRange.normalMax) validationError = '光線強度範圍設定錯誤';
      else if (motionRange.normalMin >= motionRange.normalMax) validationError = '動作偵測範圍設定錯誤';
    }
    
    if (validationError) {
      Swal.fire({
        icon: 'error',
        title: '設定錯誤',
        text: validationError,
        confirmButtonText: '確定'
      });
      return;
    }
    
    try {
      let thresholdData: any = {};
      
      if (selectedSensor.type === '電量感測器') {
        thresholdData.temperature_min = rangeSettings.power_sensor.temperature.normalMin;
        thresholdData.temperature_max = rangeSettings.power_sensor.temperature.normalMax;
        thresholdData.humidity_min = rangeSettings.power_sensor.humidity.normalMin;
        thresholdData.humidity_max = rangeSettings.power_sensor.humidity.normalMax;
        thresholdData.battery_min = rangeSettings.power_sensor.battery.normalMin;
        thresholdData.battery_max = rangeSettings.power_sensor.battery.normalMax;
      } else if (selectedSensor.type === '水位感測器') {
        thresholdData.light_min = rangeSettings.water_sensor.water_level.normalMin;
        thresholdData.light_max = rangeSettings.water_sensor.water_level.normalMax;
      } else if (selectedSensor.type === '煙霧感測器') {
        thresholdData.smoke_min = rangeSettings.smoke_sensor.smoke.normalMin;
        thresholdData.smoke_max = rangeSettings.smoke_sensor.smoke.normalMax;
      } else if (selectedSensor.type === 'RGB攝影機') {
        thresholdData.light_min = rangeSettings.rgb_camera.light.normalMin;
        thresholdData.light_max = rangeSettings.rgb_camera.light.normalMax;
        thresholdData.motion_min = rangeSettings.rgb_camera.motion.normalMin;
        thresholdData.motion_max = rangeSettings.rgb_camera.motion.normalMax;
      }
      
      await api.saveSensorThreshold(selectedSensor.id, thresholdData);
      
      try {
        const data = await api.getDashboardData();
        const updatedSensor = data.sensors.find((s: any) => s.id === selectedSensor.id);
        
        if (updatedSensor) {
          setSelectedSensor(prev => prev ? {
            ...prev,
            status: updatedSensor.status,
            alert: updatedSensor.alert
          } : null);
          
          setSensors(prev => prev.map(sensor => 
            sensor.id === selectedSensor.id 
              ? { ...sensor, status: updatedSensor.status, alert: updatedSensor.alert }
              : sensor
          ));
        }
      } catch (error) {
        console.error('更新感測器狀態失敗:', error);
      }
      
      setShowThresholdSettings(false);
      
      Swal.fire({
        icon: 'success',
        title: '保存成功！',
        text: '閾值設定已成功保存',
        confirmButtonText: '確定',
        timer: 2000,
        timerProgressBar: true
      });
    } catch (error) {
      console.error('保存閾值設定失敗:', error);
      
      Swal.fire({
        icon: 'error',
        title: '保存失敗',
        text: '保存閾值設定時發生錯誤，請重試',
        confirmButtonText: '確定'
      });
    }
  };

  // 使用 useMemo 緩存過濾後的感測器列表，避免不必要的重新計算
  const locationOptions = useMemo(() => {
    return Array.from(new Set(sensors.map(sensor => sensor.location_name))).sort();
  }, [sensors]);

  const filteredSensors = useMemo(() => {
    return sensors.filter(sensor => {
      // 狀態過濾
      if (statusFilter !== 'all' && sensor.status !== statusFilter) {
        return false;
      }
      if (selectedArea && !sensor.location_name.includes(selectedArea)) {
        return false;
      }
      if (searchQuery && !sensor.name.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      if (selectedSensorType && sensor.type !== selectedSensorType) {
        return false;
      }
      return true;
    });
  }, [sensors, statusFilter, selectedArea, searchQuery, selectedSensorType]);
  
  // 使用 useMemo 緩存圖示對象，避免每次渲染都創建新的 Icon
  // 使用 ref 來持久化圖示緩存，避免縮放時重新創建所有圖示
  const iconCacheRef = useRef<{ [key: string]: Icon }>({});
  
  const sensorIcons = useMemo(() => {
    // 只在感測器列表或縮放級別變化時更新緩存
    filteredSensors.forEach(sensor => {
      const iconKey = `${sensor.type}_${sensor.status}_${mapZoom}`;
      if (!iconCacheRef.current[iconKey]) {
        iconCacheRef.current[iconKey] = getSensorIcon(sensor.type, sensor.status, mapZoom);
      }
    });
    
    // 清理不再使用的圖示緩存（可選，避免內存洩漏）
    const currentKeys = new Set(
      filteredSensors.map(s => `${s.type}_${s.status}_${mapZoom}`)
    );
    Object.keys(iconCacheRef.current).forEach(key => {
      if (!currentKeys.has(key)) {
        delete iconCacheRef.current[key];
      }
    });
    
    return iconCacheRef.current;
  }, [filteredSensors, mapZoom]);
  
  // 處理狀態過濾器點擊
  const handleStatusFilterClick = (filter: 'all' | 'active' | 'alert') => {
    setStatusFilter(filter);
  };
  
  // 關閉警報通知
  const handleCloseAlertNotification = (id: number) => {
    setAlertNotifications(prev => prev.filter(n => n.id !== id));
  };
  

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 mb-2">載入中...</p>
          <p className="text-xs text-gray-400">如果載入時間過長，請檢查網絡連接</p>
          <button 
            onClick={() => {
              setLoading(false);
              setError('載入已取消，請點擊重試按鈕');
            }}
            className="mt-4 px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-sm"
          >
            取消載入
          </button>
        </div>
      </div>
    );
  }

  if (error && sensors.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <div className="text-red-600 text-4xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">載入失敗</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="space-y-2">
            <button 
              onClick={() => fetchSensors(true)}
              className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors"
            >
              🔄 重試載入
            </button>
            <button 
              onClick={() => {
                setError(null);
                setSensors([]);
              }}
              className="w-full px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              繼續使用（無數據模式）
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-4">
            提示：如果持續無法載入，請檢查後端服務是否正常運行
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 頂部導航欄 */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <h1 className="text-xl font-semibold text-gray-900">Forest GPS 感測器監控系統</h1>
            </div>
            <div className="flex items-center space-x-2">
              <button className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium">
                即時資訊
              </button>
              <button 
                className="px-4 py-2 bg-red-500 text-white rounded-md text-sm font-medium hover:bg-red-600 transition-colors cursor-pointer"
                onClick={() => {
                  setShowStatisticsModal(true);
                }}
              >
                統計資料
              </button>
              <button 
                className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 transition-colors cursor-pointer"
                onClick={() => setShowAddSensorModal(true)}
              >
                ➕ 新增感測器
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 主要內容區域 */}
      <div className="flex h-screen">
        {/* 左側面板 - 統計和警報 */}
        <div className="w-80 bg-white shadow-sm border-r border-gray-200 flex flex-col">
          {/* 系統統計 - 固定高度 */}
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center mb-3">
              <span className="text-lg font-semibold text-gray-900">系統統計</span>
            </div>
            <div className="space-y-3">
              <div className="bg-blue-50 p-3 rounded-lg">
                <div className="text-sm text-blue-600">總感測器數量</div>
                <div className="text-2xl font-bold text-blue-700">{sensors.length}</div>
              </div>
              <div 
                className={`p-3 rounded-lg cursor-pointer transition-all ${
                  statusFilter === 'active' 
                    ? 'bg-green-100 border-2 border-green-500' 
                    : 'bg-green-50 opacity-50'
                }`}
                onClick={() => handleStatusFilterClick('active')}
              >
                <div className="text-sm text-green-600">正常運作</div>
                <div className="text-2xl font-bold text-green-700">
                  {sensors.filter(s => s.status === 'active').length}
                </div>
              </div>
              <div 
                className={`p-3 rounded-lg cursor-pointer transition-all ${
                  statusFilter === 'alert' 
                    ? 'bg-red-100 border-2 border-red-500' 
                    : 'bg-red-50 opacity-50'
                }`}
                onClick={() => handleStatusFilterClick('alert')}
              >
                <div className="text-sm text-red-600">警報狀態</div>
                <div className="text-2xl font-bold text-red-700">
                  {sensors.filter(s => s.status === 'alert').length}
                </div>
              </div>
            </div>
          </div>

          {/* 警報感測器列表 */}
          {sensors.filter(s => s.status === 'alert').length > 0 && (
            <div className="flex-1 overflow-y-auto border-b border-gray-200">
              <div className="p-3 bg-red-50 border-b border-red-100">
                <span className="text-sm font-semibold text-red-700">警報中的感測器</span>
              </div>
              <div className="divide-y divide-gray-100">
                {sensors
                  .filter(s => s.status === 'alert')
                  .sort((a, b) => {
                    const tA = (a as any).alert_triggered_at || a.lastUpdate || '';
                    const tB = (b as any).alert_triggered_at || b.lastUpdate || '';
                    return tB > tA ? 1 : tB < tA ? -1 : 0;
                  })
                  .map(sensor => (
                  <div
                    key={sensor.id}
                    className="p-3 hover:bg-red-50 cursor-pointer transition-colors"
                    onClick={() => handleAlertClick(sensor)}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-red-500 text-lg animate-pulse">🚨</span>
                      <span className="font-semibold text-sm text-gray-800">{sensor.name}</span>
                    </div>
                    <div className="text-xs text-gray-500 ml-7">{sensor.location_name}</div>
                    <div className="text-xs text-red-600 font-medium ml-7 mt-1">{sensor.alert}</div>
                    <div className="text-xs text-gray-400 ml-7 mt-1">
                      {(sensor as any).alert_triggered_at
                        ? new Date((sensor as any).alert_triggered_at).toLocaleString('zh-TW', {
                            year: 'numeric', month: '2-digit', day: '2-digit',
                            hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
                          })
                        : sensor.lastUpdate}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>

        {/* 中央地圖區域 */}
        <div className="flex-1 flex flex-col">
          {/* 搜尋和篩選控制項 */}
          <div className="bg-white border-b border-gray-200 p-4">
            <div className="flex items-center space-x-4">
                             <div className="flex-1">
                 <label className="block text-sm font-medium text-gray-700 mb-1">區域</label>
                 <select
                   value={selectedArea}
                   onChange={(e) => setSelectedArea(e.target.value)}
                   className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                 >
                   <option value="">全部區域</option>
                   {locationOptions.map((location, index) => (
                     <option key={index} value={location}>
                       {location}
                     </option>
                   ))}
                 </select>
               </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">感測器類型</label>
                <select
                  value={selectedSensorType}
                  onChange={(e) => setSelectedSensorType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">全部類型</option>
                  <option value="電量感測器">電量感測器</option>
                  <option value="水位感測器">水位感測器</option>
                  <option value="煙霧感測器">煙霧感測器</option>
                  <option value="RGB攝影機">RGB攝影機</option>
                </select>
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">搜尋感測器</label>
                <input
                  type="text"
                  placeholder="輸入感測器名稱..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* 地圖容器 */}
          <div className="flex-1 relative">
            <MapContainer
              center={mapCenter}
              zoom={mapZoom}
              className="h-full w-full"
              zoomControl={true}
              ref={mapRef}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
              <MapAutoFit sensors={sensors} />

              {filteredSensors.map((sensor) => {
                // 使用緩存的圖示，避免每次渲染都創建新的 Icon 對象
                const iconKey = `${sensor.type}_${sensor.status}_${mapZoom}`;
                const icon = sensorIcons[iconKey] || getSensorIcon(sensor.type, sensor.status, mapZoom);
                
                return (
                  <Marker
                    key={sensor.id}
                    position={sensor.coordinates}
                    icon={icon}
                    eventHandlers={{
                      click: () => handleSensorClick(sensor),
                      mouseover: (e) => handleMouseEnter(sensor, e),
                      mouseout: handleMouseLeave,
                    }}
                  />
                );
              })}
            </MapContainer>
          </div>
        </div>

        {/* 右側面板 - 感測器詳情 */}
        {selectedSensor && (
          <SensorDetailPanel
            selectedSensor={selectedSensor}
            showThresholdSettings={showThresholdSettings}
            setShowThresholdSettings={setShowThresholdSettings}
            hlsUrl={hlsUrl}
            streamFullscreen={streamFullscreen}
            setStreamFullscreen={setStreamFullscreen}
            hlsVideoRef={hlsVideoRef}
            onClose={handleCloseInfo}
            rangeSettings={rangeSettings}
            handleRangeChange={handleRangeChange}
            handleSaveThreshold={handleSaveThreshold}
            RangeSlider={RangeSlider}
          />
        )}
      </div>

      {/* 懸停提示 */}
      {hoveredSensor && (
        <SensorHoverTooltip hoveredSensor={hoveredSensor} hoverPosition={hoverPosition} />
      )}

      {/* 串流懸浮視窗 */}
      {streamFullscreen && hlsUrl && (
        <StreamOverlay
          hlsUrl={hlsUrl}
          selectedSensor={selectedSensor}
          onClose={() => setStreamFullscreen(false)}
        />
      )}

      {/* 統計資料彈窗 */}
      {showStatisticsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center" style={{ zIndex: 9999 }}>
          <div className="bg-white rounded-lg shadow-xl w-11/12 max-w-6xl max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">統計資料</h2>
              <button
                onClick={() => setShowStatisticsModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
              >
                ×
              </button>
            </div>

            <div className="p-6">
              {/* 篩選條件 */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">起始時間</label>
                  <input
                    type="datetime-local"
                    value={statisticsParams.start_time.slice(0, 16)}
                    onChange={(e) => handleStatisticsParamChange('start_time', e.target.value + ':00Z')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">結束時間</label>
                  <input
                    type="datetime-local"
                    value={statisticsParams.end_time.slice(0, 16)}
                    onChange={(e) => handleStatisticsParamChange('end_time', e.target.value + ':00Z')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">區域</label>
                  <select
                    value={statisticsParams.area}
                    onChange={(e) => handleStatisticsParamChange('area', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">全部區域</option>
                    {areaOptions.map(option => (
                      <option key={option.value} value={option.label}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">感測器種類</label>
                  <select
                    value={statisticsParams.sensor_type}
                    onChange={(e) => handleStatisticsParamChange('sensor_type', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">全部種類</option>
                    {sensorTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* 查詢按鈕 */}
              <div className="flex justify-center mb-6">
                <button
                  onClick={fetchStatisticsData}
                  disabled={statisticsLoading}
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {statisticsLoading ? '查詢中...' : '查詢統計資料'}
                </button>
              </div>

              {/* 統計結果 */}
              {statisticsData && (
                <div className="space-y-6">
                  {/* 統計摘要 */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <div className="text-sm text-blue-600">感測器數量</div>
                      <div className="text-2xl font-bold text-blue-700">{statisticsData.sensor_count}</div>
                    </div>
                    <div className="bg-red-50 p-4 rounded-lg">
                      <div className="text-sm text-red-600">警報事件數量</div>
                      <div className="text-2xl font-bold text-red-700">{statisticsData.alert_count}</div>
                    </div>
                  </div>

                  {/* 感測器列表 */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">感測器列表</h3>
                    <div className="bg-gray-50 rounded-lg overflow-hidden">
                      <div className="overflow-x-auto max-h-40 overflow-y-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-100 sticky top-0">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">名稱</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">類型</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">位置</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">狀態</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">建立時間</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {statisticsData.sensors.map((sensor) => (
                              <tr key={sensor.id} className="hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm text-gray-900">{sensor.id}</td>
                                <td className="px-4 py-3 text-sm text-gray-900">{sensor.name}</td>
                                <td className="px-4 py-3 text-sm text-gray-600">{sensor.type}</td>
                                <td className="px-4 py-3 text-sm text-gray-600">{sensor.location_name}</td>
                                <td className="px-4 py-3 text-sm">
                                  <span className={`px-2 py-1 rounded text-xs ${
                                    sensor.status === 'active' 
                                      ? 'bg-green-100 text-green-800' 
                                      : 'bg-gray-100 text-gray-800'
                                  }`}>
                                    {sensor.status === 'active' ? '正常' : '警報'}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-500">{sensor.created_at}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {/* 警報事件列表 */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">警報事件列表</h3>
                    <div className="bg-gray-50 rounded-lg overflow-hidden">
                      <div className="overflow-x-auto max-h-80 overflow-y-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-100 sticky top-0">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">感測器</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">類型</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">嚴重程度</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">狀態</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">訊息</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">觸發時間</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {statisticsData.alerts.map((alert) => (
                              <tr key={alert.id} className="hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm text-gray-900">{alert.id}</td>
                                <td className="px-4 py-3 text-sm text-gray-900">{alert.sensor_name}</td>
                                <td className="px-4 py-3 text-sm text-gray-600">{alert.alert_type}</td>
                                <td className="px-4 py-3 text-sm">
                                  <span className={`px-2 py-1 rounded text-xs ${
                                    alert.severity === 'critical' ? 'bg-red-100 text-red-800' :
                                    alert.severity === 'high' ? 'bg-orange-100 text-orange-800' :
                                    alert.severity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-blue-100 text-blue-800'
                                  }`}>
                                    {alert.severity}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-sm">
                                  <span className={`px-2 py-1 rounded text-xs ${
                                    alert.status === 'active' ? 'bg-red-100 text-red-800' :
                                    alert.status === 'resolved' ? 'bg-green-100 text-green-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {alert.status}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">{alert.message}</td>
                                <td className="px-4 py-3 text-sm text-gray-500">{alert.triggered_at}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 新增感測器彈窗 */}
      {showAddSensorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center" style={{ zIndex: 10000 }}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* 標題 */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">新增感測器</h2>
                <button
                  onClick={() => setShowAddSensorModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>

              {/* 表單 */}
              <div className="space-y-4">
                {/* 感測器名稱 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    感測器名稱 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newSensorForm.name}
                    onChange={(e) => setNewSensorForm({ ...newSensorForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="例如：RGB 攝影機 #001"
                  />
                </div>

                {/* 感測器類型 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    感測器類型 <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={newSensorForm.type}
                    onChange={(e) => setNewSensorForm({ ...newSensorForm, type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    {sensorTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>

                {/* 位置名稱 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    位置名稱
                  </label>
                  <input
                    type="text"
                    value={newSensorForm.location_name}
                    onChange={(e) => setNewSensorForm({ ...newSensorForm, location_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="例如：核心區域彩色監測"
                  />
                </div>

                {/* 地圖選擇位置 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    選擇位置 <span className="text-red-500">*</span>
                    <span className="text-xs text-gray-500 ml-2">（點選地圖選擇位置，或手動輸入經緯度）</span>
                  </label>
                  <div className="border border-gray-300 rounded-md overflow-hidden" style={{ height: '300px' }}>
                    <MapContainer
                      center={selectedLocation || [25.0330, 121.5654]}
                      zoom={13}
                      style={{ height: '100%', width: '100%' }}
                      ref={addSensorMapRef}
                    >
                      <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      />
                      <MapClickHandler
                        onMapClick={(lat, lng) => {
                          setSelectedLocation([lat, lng]);
                          setNewSensorForm({
                            ...newSensorForm,
                            latitude: lat.toFixed(6),
                            longitude: lng.toFixed(6),
                          });
                        }}
                      />
                      {selectedLocation && (
                        <Marker position={selectedLocation}>
                          <Popup>
                            選中的位置<br />
                            緯度: {selectedLocation[0].toFixed(6)}<br />
                            經度: {selectedLocation[1].toFixed(6)}
                          </Popup>
                        </Marker>
                      )}
                    </MapContainer>
                  </div>
                </div>

                {/* 緯度 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    緯度 (Latitude) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="any"
                    value={newSensorForm.latitude}
                    onChange={(e) => {
                      const lat = e.target.value;
                      setNewSensorForm({ ...newSensorForm, latitude: lat });
                      if (lat && newSensorForm.longitude) {
                        const latNum = parseFloat(lat);
                        const lngNum = parseFloat(newSensorForm.longitude);
                        if (!isNaN(latNum) && !isNaN(lngNum)) {
                          setSelectedLocation([latNum, lngNum]);
                          // 更新地圖中心
                          if (addSensorMapRef.current) {
                            (addSensorMapRef.current as any).setView([latNum, lngNum], 13);
                          }
                        }
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="例如：24.765000"
                  />
                </div>

                {/* 經度 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    經度 (Longitude) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="any"
                    value={newSensorForm.longitude}
                    onChange={(e) => {
                      const lng = e.target.value;
                      setNewSensorForm({ ...newSensorForm, longitude: lng });
                      if (newSensorForm.latitude && lng) {
                        const latNum = parseFloat(newSensorForm.latitude);
                        const lngNum = parseFloat(lng);
                        if (!isNaN(latNum) && !isNaN(lngNum)) {
                          setSelectedLocation([latNum, lngNum]);
                          // 更新地圖中心
                          if (addSensorMapRef.current) {
                            (addSensorMapRef.current as any).setView([latNum, lngNum], 13);
                          }
                        }
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="例如：121.000500"
                  />
                </div>

                {/* IP 地址 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    IP 地址
                  </label>
                  <input
                    type="text"
                    value={newSensorForm.ip}
                    onChange={(e) => setNewSensorForm({ ...newSensorForm, ip: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="例如：192.168.1.100"
                  />
                </div>

                {/* 攝影機連線資訊（僅 RGB 攝影機顯示） */}
                {newSensorForm.type === 'RGB攝影機' && (
                  <div className="border-t pt-4 mt-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">攝影機連線資訊</h3>
                    
                    {/* 攝影機種類選擇 */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        攝影機種類
                      </label>
                      <div className="flex gap-4">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            value="ip"
                            checked={newSensorForm.camera_type === 'ip'}
                            onChange={(e) => setNewSensorForm({ ...newSensorForm, camera_type: e.target.value as 'ip' | 'nvr' })}
                            className="mr-2"
                          />
                          <span>IP 攝影機</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            value="nvr"
                            checked={newSensorForm.camera_type === 'nvr'}
                            onChange={(e) => setNewSensorForm({ ...newSensorForm, camera_type: e.target.value as 'ip' | 'nvr' })}
                            className="mr-2"
                          />
                          <span>NVR 攝影機</span>
                        </label>
                      </div>
                    </div>

                    {/* IP 攝影機配置 */}
                    {newSensorForm.camera_type === 'ip' && (
                      <div className="space-y-4 bg-blue-50 p-4 rounded-md">
                        <h4 className="text-md font-semibold text-gray-800 mb-3">IP 攝影機設定</h4>
                        
                        {/* RTSP URL（可選，如果提供則優先使用） */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            RTSP URL（選填，如果提供則優先使用）
                          </label>
                          <input
                            type="text"
                            value={newSensorForm.rtsp_url}
                            onChange={(e) => setNewSensorForm({ ...newSensorForm, rtsp_url: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                            placeholder="例如：rtsp://111.70.11.122:1554/stream0"
                          />
                          <p className="text-xs text-gray-500 mt-1">如果提供完整的 RTSP URL，將直接使用此 URL</p>
                        </div>

                        <div className="text-sm text-gray-600 mb-2">或填寫以下資訊自動構建 RTSP URL：</div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            IP 地址 <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={newSensorForm.ip_address}
                            onChange={(e) => setNewSensorForm({ ...newSensorForm, ip_address: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                            placeholder="例如：111.70.45.14"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Port
                          </label>
                          <input
                            type="text"
                            value={newSensorForm.ip_port}
                            onChange={(e) => setNewSensorForm({ ...newSensorForm, ip_port: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                            placeholder="例如：1554"
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              帳號
                            </label>
                            <input
                              type="text"
                              value={newSensorForm.ipcam_username}
                              onChange={(e) => setNewSensorForm({ ...newSensorForm, ipcam_username: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                              placeholder="admin"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              密碼
                            </label>
                            <input
                              type="password"
                              value={newSensorForm.ipcam_password}
                              onChange={(e) => setNewSensorForm({ ...newSensorForm, ipcam_password: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                              placeholder="Pass1234"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* NVR 攝影機配置 */}
                    {newSensorForm.camera_type === 'nvr' && (
                      <div className="space-y-4 bg-green-50 p-4 rounded-md">
                        <h4 className="text-md font-semibold text-gray-800 mb-3">NVR 攝影機設定</h4>
                        
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          NVR 主機地址
                        </label>
                        <input
                          type="text"
                          value={newSensorForm.nvr_host}
                          onChange={(e) => setNewSensorForm({ ...newSensorForm, nvr_host: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                          placeholder="例如：140.118.156.118"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            NVR 連接埠
                          </label>
                          <input
                            type="number"
                            value={newSensorForm.nvr_port}
                            onChange={(e) => setNewSensorForm({ ...newSensorForm, nvr_port: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                            placeholder="7001"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            使用者名稱
                          </label>
                          <input
                            type="text"
                            value={newSensorForm.nvr_username}
                            onChange={(e) => setNewSensorForm({ ...newSensorForm, nvr_username: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                            placeholder="admin"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          密碼
                        </label>
                        <input
                          type="password"
                          value={newSensorForm.nvr_password}
                          onChange={(e) => setNewSensorForm({ ...newSensorForm, nvr_password: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                            placeholder=""
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            NVR 攝影機 ID
                        </label>
                        <input
                          type="text"
                          value={newSensorForm.nvr_camera_id}
                          onChange={(e) => setNewSensorForm({ ...newSensorForm, nvr_camera_id: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                            placeholder="例如：camera-001"
                        />
                      </div>
                    </div>
                    )}
                  </div>
                )}

                {/* 按鈕 */}
                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    onClick={() => setShowAddSensorModal(false)}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                  >
                    取消
                  </button>
                  <button
                    onClick={handleAddSensor}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                  >
                    新增
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 警報通知由 alert-overlay.js 處理 */}
    </div>
  );
}

export default App; 